/**
 * DeepL 번역 클라이언트
 *
 * 용도: 카카오맵 검색 결과(place_name, address)를 UI 언어로 실시간 번역.
 *       정적 PLACE_NAMES_MAP 사전으로 처리되지 않은 긴 꼬리(long-tail) 장소명과
 *       도로명/지번 주소를 DeepL Free API로 번역한다.
 *
 * 설계 포인트:
 *   1) 배치 요청 — DeepL `text` 파라미터에 배열을 넣으면 한 요청으로 여러 건 처리 (쿼터 절약)
 *   2) 2단계 캐시 — 메모리 Map(세션 내 즉시 반환) + localStorage(세션 간 영속)
 *   3) 타겟이 ko 이면 호출 스킵 (원문 한국어 그대로 반환)
 *   4) 엔드포인트는 config에서 주입 → 추후 백엔드 프록시로 전환 시 .env만 수정
 */

import config from '../config';

const CACHE_KEY_PREFIX = 'vvt_deepl_cache_';
const CACHE_MAX_ENTRIES = 2000; // localStorage 폭주 방지

// DeepL은 UI 언어 코드와 약간 다름: zh-CN → ZH, en → EN-US 권장
const DEEPL_LANG_MAP = {
  en: 'EN-US',
  zh: 'ZH',
  ja: 'JA',
  es: 'ES',
  ko: 'KO',
};

// 메모리 캐시 (탭/세션 생존)
const memoryCache = new Map(); // key: `${targetLang}|${text}` → translated

function cacheKey(targetLang, text) {
  return `${targetLang}|${text}`;
}

function loadPersistCache(targetLang) {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + targetLang);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePersistCache(targetLang, obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > CACHE_MAX_ENTRIES) {
      // LRU 근사: 오래된 키 제거 (삽입 순서 유지 가정)
      const trimmed = {};
      keys.slice(-CACHE_MAX_ENTRIES).forEach((k) => {
        trimmed[k] = obj[k];
      });
      obj = trimmed;
    }
    localStorage.setItem(CACHE_KEY_PREFIX + targetLang, JSON.stringify(obj));
  } catch {
    // 쿼터 초과 등은 무시 (번역은 계속 동작)
  }
}

function getCached(targetLang, text) {
  const mKey = cacheKey(targetLang, text);
  if (memoryCache.has(mKey)) return memoryCache.get(mKey);

  const persist = loadPersistCache(targetLang);
  if (text in persist) {
    memoryCache.set(mKey, persist[text]);
    return persist[text];
  }
  return undefined;
}

function putCached(targetLang, entries) {
  const persist = loadPersistCache(targetLang);
  entries.forEach(([text, translated]) => {
    memoryCache.set(cacheKey(targetLang, text), translated);
    persist[text] = translated;
  });
  savePersistCache(targetLang, persist);
}

/**
 * DeepL API 원시 호출 (배치)
 * @param {string[]} texts - 번역할 문자열 배열
 * @param {string} deeplTarget - DEEPL_LANG_MAP 변환 후 값 (예: 'EN-US')
 * @returns {Promise<string[]>} 동일 순서의 번역 결과
 */
// 로컬/백엔드 프록시 경로면 Authorization 주입을 서버측에 위임 (키가 브라우저 번들에 포함되지 않음).
// DeepL 직접 엔드포인트면 브라우저에서 Authorization 헤더를 붙여야 함.
function isProxyUrl(url) {
  return !!url && !/^https?:\/\//i.test(url);
}

async function callDeepL(texts, deeplTarget) {
  const { DEEPL_API_KEY, DEEPL_API_URL } = config;
  const viaProxy = isProxyUrl(DEEPL_API_URL);
  if (!viaProxy && !DEEPL_API_KEY) {
    console.warn('[DeepL] API 키 미설정 — 번역 스킵');
    return texts;
  }

  const body = new URLSearchParams();
  texts.forEach((t) => body.append('text', t));
  body.append('target_lang', deeplTarget);
  body.append('source_lang', 'KO'); // 카카오 응답은 항상 한국어

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  if (!viaProxy) headers.Authorization = `DeepL-Auth-Key ${DEEPL_API_KEY}`;

  const res = await fetch(DEEPL_API_URL, {
    method: 'POST',
    headers,
    body: body.toString(),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`DeepL ${res.status}: ${errText}`);
  }
  const data = await res.json();
  return (data.translations || []).map((t) => t.text);
}

/**
 * HTML 단일 문서 번역 — DeepL `tag_handling=html` 사용.
 * 약관/정책처럼 긴 HTML 본문(<h3>/<p>/<table>/<ul> 등)을 태그 구조를 보존한 채 번역.
 * 긴 문서를 보호하기 위해 lang별로 별도 localStorage 캐시(`vvt_deepl_html_<lang>_<hash>`)에 저장.
 */
function simpleHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
}

export async function translateHtml(html, targetLang) {
  if (!html || targetLang === 'ko' || !targetLang) return html;
  const deeplTarget = DEEPL_LANG_MAP[targetLang];
  if (!deeplTarget) return html;

  const cacheKey = `vvt_deepl_html_${targetLang}_${simpleHash(html)}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch { /* ignore */ }

  const { DEEPL_API_KEY, DEEPL_API_URL } = config;
  const viaProxy = isProxyUrl(DEEPL_API_URL);
  if (!viaProxy && !DEEPL_API_KEY) {
    console.warn('[DeepL] API 키 미설정 — HTML 번역 스킵');
    return html;
  }

  const body = new URLSearchParams();
  body.append('text', html);
  body.append('target_lang', deeplTarget);
  body.append('source_lang', 'KO');
  body.append('tag_handling', 'html');

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  if (!viaProxy) headers.Authorization = `DeepL-Auth-Key ${DEEPL_API_KEY}`;

  try {
    const res = await fetch(DEEPL_API_URL, {
      method: 'POST',
      headers,
      body: body.toString(),
    });
    if (!res.ok) throw new Error(`DeepL ${res.status}`);
    const data = await res.json();
    const translated = data.translations?.[0]?.text || html;
    try { localStorage.setItem(cacheKey, translated); } catch { /* quota */ }
    return translated;
  } catch (err) {
    console.warn('[DeepL] HTML 번역 실패:', err.message);
    return html;
  }
}

/**
 * 단일 문자열 번역 (내부적으로 translateBatch 호출)
 * @param {string} text
 * @param {string} targetLang - UI 언어 코드 ('en' | 'zh' | 'ja' | 'es' | 'ko')
 * @returns {Promise<string>}
 */
export async function translateText(text, targetLang) {
  const [result] = await translateBatch([text], targetLang);
  return result;
}

/**
 * #18: 사용자 입력(외국어) → 한국어 변환.
 *  카카오 키워드 API는 한국어 DB라 외국어 그대로 보내면 결과 0건.
 *  소스 언어 자동 감지로 한국어로 변환 후 검색에 사용.
 *
 *  - 입력이 이미 한글 위주면 호출 스킵 (원문 반환).
 *  - 캐시 키 prefix `q2ko_` 로 분리(일반 KO→target 캐시와 충돌 방지).
 *  - 실패 시 원문 반환 (검색 자체는 계속 동작).
 */
const HANGUL_RE = /[가-힣]/;
const Q2KO_CACHE_KEY = 'vvt_deepl_q2ko_cache';

function loadQ2KoCache() {
  try {
    const raw = localStorage.getItem(Q2KO_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveQ2KoCache(obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > CACHE_MAX_ENTRIES) {
      const trimmed = {};
      keys.slice(-CACHE_MAX_ENTRIES).forEach((k) => { trimmed[k] = obj[k]; });
      obj = trimmed;
    }
    localStorage.setItem(Q2KO_CACHE_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
}

export async function translateQueryToKorean(query) {
  const text = (query || '').trim();
  if (!text) return text;
  // 한글이 1자 이상 포함되면 이미 한국어 입력으로 판단 → 그대로 사용
  if (HANGUL_RE.test(text)) return text;

  const cache = loadQ2KoCache();
  if (text in cache) return cache[text];

  const { DEEPL_API_KEY, DEEPL_API_URL } = config;
  const viaProxy = isProxyUrl(DEEPL_API_URL);
  if (!viaProxy && !DEEPL_API_KEY) {
    return text;
  }

  const body = new URLSearchParams();
  body.append('text', text);
  body.append('target_lang', 'KO');
  // source_lang 미지정 → DeepL 자동 감지 (ja/zh/en 등)

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  if (!viaProxy) headers.Authorization = `DeepL-Auth-Key ${DEEPL_API_KEY}`;

  try {
    const res = await fetch(DEEPL_API_URL, {
      method: 'POST',
      headers,
      body: body.toString(),
    });
    if (!res.ok) throw new Error(`DeepL ${res.status}`);
    const data = await res.json();
    const translated = data.translations?.[0]?.text || text;
    cache[text] = translated;
    saveQ2KoCache(cache);
    return translated;
  } catch (err) {
    console.warn('[DeepL] 쿼리→한국어 번역 실패:', err.message);
    return text;
  }
}

/**
 * 배열 번역 — 캐시 조회 후 미스된 것만 한 번에 API 호출.
 * API 실패 시 원문을 그대로 반환 (UI가 깨지지 않도록).
 *
 * @param {string[]} texts
 * @param {string} targetLang - UI 언어 코드
 * @returns {Promise<string[]>} 동일 순서의 번역/원문 문자열
 */
/**
 * 요청 코얼레싱(coalescing) + 직렬화 + 429 재시도:
 *   - 여러 컴포넌트가 동시에/순차적으로 translateBatch 호출해도 COALESCE_MS 창 동안 모음
 *   - 전역 mutex: 한 번에 DeepL 호출 1건만 in-flight (Free 플랜 동시성 제한 회피)
 *   - 호출 간 MIN_INTERVAL_MS 강제 간격 (초당 rate limit 회피)
 *   - 429 수신 시 지수 백오프 재시도 (최대 3회)
 *   - in-flight dedupe: 같은 문자열은 동일 Promise 공유
 */
// BugHerd #18 후속: 첫 검색 깜빡임(한국어 → 번역) 단축.
// 정적 사전(PLACE_NAMES_MAP) 미스 시에만 DeepL 호출되므로, 호출 자체 지연을 줄여
// 사용자가 인지하는 깜빡임 시간을 약 500ms → 약 150ms 수준으로 축소.
const COALESCE_MS = 80;         // 250 → 80 (여러 컴포넌트 동시 호출 모음 + 첫 응답 단축)
const MIN_INTERVAL_MS = 250;    // 600 → 250 (DeepL Free 플랜 50req/min 한도 내)
const MAX_BATCH_TEXTS = 50;     // 한 요청에 묶는 텍스트 수 상한
const MAX_RETRIES = 3;

// targetLang → { texts:Set, resolvers:Map<text, Array<{resolve, reject}>>, timer, deeplTarget }
const pendingByLang = new Map();

// 직렬화용: 마지막 DeepL 호출 완료(or 예약된 다음 slot) 시각
let nextAvailableAt = 0;
let chain = Promise.resolve();

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

function schedulePending(targetLang, deeplTarget) {
  let bucket = pendingByLang.get(targetLang);
  if (!bucket) {
    bucket = { texts: new Set(), resolvers: new Map(), timer: null, deeplTarget };
    pendingByLang.set(targetLang, bucket);
  }
  if (bucket.timer) return bucket;
  bucket.timer = setTimeout(() => {
    // 체인에 연결해 직렬 실행
    chain = chain.then(() => flushPending(targetLang));
  }, COALESCE_MS);
  return bucket;
}

async function callDeepLWithRetry(texts, deeplTarget) {
  let attempt = 0;
  while (true) {
    // rate-limit gate
    const now = Date.now();
    const wait = nextAvailableAt - now;
    if (wait > 0) await sleep(wait);
    nextAvailableAt = Date.now() + MIN_INTERVAL_MS;

    try {
      return await callDeepL(texts, deeplTarget);
    } catch (err) {
      const is429 = /DeepL 429/.test(err.message);
      if (is429 && attempt < MAX_RETRIES) {
        const backoff = 800 * Math.pow(2, attempt); // 800, 1600, 3200
        console.warn(`[DeepL] 429 — ${backoff}ms 후 재시도 (${attempt + 1}/${MAX_RETRIES})`);
        await sleep(backoff);
        attempt++;
        continue;
      }
      throw err;
    }
  }
}

async function flushPending(targetLang) {
  const bucket = pendingByLang.get(targetLang);
  if (!bucket) return;
  pendingByLang.delete(targetLang);
  clearTimeout(bucket.timer);

  const allTexts = [...bucket.texts];
  const { resolvers, deeplTarget } = bucket;

  const chunks = [];
  for (let i = 0; i < allTexts.length; i += MAX_BATCH_TEXTS) {
    chunks.push(allTexts.slice(i, i + MAX_BATCH_TEXTS));
  }

  const all = {};
  try {
    for (const chunk of chunks) {
      const out = await callDeepLWithRetry(chunk, deeplTarget);
      chunk.forEach((src, i) => { all[src] = out[i] ?? src; });
    }
    const entries = Object.entries(all).filter(([src, t]) => t && t !== src);
    if (entries.length > 0) putCached(targetLang, entries);

    resolvers.forEach((waiters, src) => {
      const t = all[src] ?? src;
      waiters.forEach((w) => w.resolve(t));
    });
  } catch (err) {
    console.warn('[DeepL] 번역 실패 — 원문 유지:', err.message);
    resolvers.forEach((waiters, src) => {
      waiters.forEach((w) => w.resolve(src));
    });
  }
}

function enqueueSingle(src, targetLang, deeplTarget) {
  return new Promise((resolve, reject) => {
    const bucket = schedulePending(targetLang, deeplTarget);
    bucket.texts.add(src);
    const arr = bucket.resolvers.get(src) || [];
    arr.push({ resolve, reject });
    bucket.resolvers.set(src, arr);
  });
}

export async function translateBatch(texts, targetLang) {
  if (!Array.isArray(texts) || texts.length === 0) return [];
  if (targetLang === 'ko' || !targetLang) return texts.slice();

  const deeplTarget = DEEPL_LANG_MAP[targetLang];
  if (!deeplTarget) return texts.slice();

  const result = new Array(texts.length);
  const pendingPromises = [];

  texts.forEach((txt, i) => {
    if (!txt || typeof txt !== 'string') {
      result[i] = txt;
      return;
    }
    const cached = getCached(targetLang, txt);
    if (cached !== undefined) {
      result[i] = cached;
      return;
    }
    pendingPromises.push(
      enqueueSingle(txt, targetLang, deeplTarget).then((translated) => {
        result[i] = translated ?? txt;
      })
    );
  });

  if (pendingPromises.length > 0) {
    await Promise.all(pendingPromises);
  }
  return result;
}
